/**
 * SDK React 渲染到 Shadow DOM 的工具(B5/B9)
 *
 * - mountShadowApp:在 shadow root 注入 <style> + 建 mount 容器 + createRoot 渲染,
 *   返回控制器(re-render / unmount)。React 19 支持渲染到任意 Node,这里渲染到
 *   shadow 内的 div(非 shadow root 本身),便于 <style> 与 React 树分离。
 * - SdkErrorBoundary:React 错误边界,兜住渲染异常降级文案,不波及宿主首页(B9)。
 */
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { SDK_STYLE } from './styles';
import { SDK_STYLE_H5 } from './styles.h5';

/** 渲染控制器 */
export interface ShadowAppHandle {
  /** 用新 React 节点重渲染(属性变化时调用) */
  render(node: React.ReactNode): void;
  /** 卸载 React 树并清空 mount 容器 */
  unmount(): void;
}

/**
 * 在 shadow root 内挂载 React 应用。
 * 注入一次性 <style>(样式隔离三招),React 只管理 mount 容器内部,避免重渲染抖动样式。
 */
export function mountShadowApp(shadow: ShadowRoot, node: React.ReactNode): ShadowAppHandle {
  // 1. 注入样式(幂等:仅当不存在时插入)
  if (!shadow.querySelector('style[data-hh-sdk]')) {
    const style = document.createElement('style');
    style.setAttribute('data-hh-sdk', '');
    style.textContent = SDK_STYLE + SDK_STYLE_H5;
    shadow.appendChild(style);
  }

  // 2. mount 容器
  const mount = document.createElement('div');
  mount.className = 'hh-root';
  shadow.appendChild(mount);

  // 3. React 渲染(包错误边界)
  const root = createRoot(mount);
  root.render(<SdkErrorBoundary>{node}</SdkErrorBoundary>);

  let current = node;
  return {
    render(next: React.ReactNode) {
      current = next;
      root.render(<SdkErrorBoundary>{next}</SdkErrorBoundary>);
    },
    unmount() {
      root.unmount();
      mount.remove();
      void current; // 保留引用语义,避免 lint 误报
    },
  };
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/** SDK 内部错误边界:渲染异常时降级为文案,不抛到宿主 */
export class SdkErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    console.error('[HotelSDK] 组件渲染异常:', error);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <div className="hh-error">加载失败,请稍后重试</div>;
    }
    return this.props.children;
  }
}
